import { useTokchanStore } from '@/store/tokchanStore.js';

interface FuzzyConfig {
    minSimilarity: number;
    exactMatchBonus: number;
    categoryWeighting: number;
    formatPreference: string[];
}

interface AssetFormat {
    path: string;
}

interface Asset {
    base_name: string;
    formats: Record<string, AssetFormat>;
}

interface EffectCatalogData {
    categories: Record<string, Asset[]>;
}

interface MediaPathRegistryWithConfigs {
    configs?: {
        effects?: EffectCatalogData;
    };
}

class SmartAssetResolver {
    private fuzzyMatchCache: Map<string, Asset | null> = new Map();
    private normalizationCache: Map<string, string> = new Map();
    private aliasMap: Map<string, string> = new Map();
    private normalizationRules: { [key: string]: (str: string) => string };
    private fuzzyConfig: FuzzyConfig;

    constructor() {
        this.normalizationRules = {
            trimSpaces: (str) => str.trim(),
            removeTrailingHyphens: (str) => str.replace(/[-\s]*$/, ''),
            standardizeSpaces: (str) => str.replace(/\s+/g, ' '),
            removePunctuation: (str) => str.replace(/[^\w\s]/g, ''),
            padNumbers: (str) => str.replace(/\b(\d+)\b/g, (_match, num) =>
                num.length < 2 ? num.padStart(2, '0') : num
            ),
            electricityNormalize: (str) => {
                return str
                    .replace(/^Electric\s+discharge\s+(\d+)$/i, 'Electricity $1')
                    .replace(/^Electricity\s+(\d+)\s+(.+)$/i, 'Electricity $1 $2');
            }
        };

        this.fuzzyConfig = {
            minSimilarity: 0.7,
            exactMatchBonus: 0.3,
            categoryWeighting: 0.1,
            formatPreference: ['webm', 'mp4']
        };

        this.buildCommonAliases();
    }

    public async resolveAsset(category: string, assetName: string, preferredFormat: string = 'webm', catalog: EffectCatalogData | null = null): Promise<Asset | null> {
        const cacheKey = `${category}:${assetName}:${preferredFormat}`;
        if (this.fuzzyMatchCache.has(cacheKey)) {
            return this.fuzzyMatchCache.get(cacheKey)!;
        }

        const effectCatalog = catalog || this.getEffectCatalog();
        if (!effectCatalog?.categories?.[category]) {
            return null;
        }

        const categoryEffects: Asset[] = effectCatalog.categories[category];
        if (!Array.isArray(categoryEffects)) {
            return null; // Or handle detailed catalog loading
        }

        const exactMatch = this.findExactMatch(categoryEffects, assetName, preferredFormat);
        if (exactMatch) {
            this.fuzzyMatchCache.set(cacheKey, exactMatch);
            return exactMatch;
        }

        const normalizedMatch = this.findNormalizedMatch(categoryEffects, assetName, preferredFormat);
        if (normalizedMatch) {
            this.fuzzyMatchCache.set(cacheKey, normalizedMatch);
            return normalizedMatch;
        }

        const fuzzyMatch = this.findFuzzyMatch(categoryEffects, assetName, preferredFormat);
        if (fuzzyMatch) {
            this.fuzzyMatchCache.set(cacheKey, fuzzyMatch.effect);
            return fuzzyMatch.effect;
        }

        return null;
    }

    private findExactMatch(effects: Asset[], assetName: string, preferredFormat: string): Asset | null {
        const exactEffect = effects.find(effect => effect.base_name === assetName);
        return exactEffect?.formats?.[preferredFormat] ? exactEffect : null;
    }

    private findNormalizedMatch(effects: Asset[], assetName: string, preferredFormat: string): Asset | null {
        const normalizedTarget = this.normalizeAssetName(assetName);
        for (const effect of effects) {
            const normalizedEffectName = this.normalizeAssetName(effect.base_name);
            if (normalizedEffectName === normalizedTarget) {
                if (effect.formats?.[preferredFormat]) {
                    return effect;
                }
            }
        }
        return null;
    }

    private findFuzzyMatch(effects: Asset[], assetName: string, preferredFormat: string): { effect: Asset; score: number } | null {
        const normalizedTarget = this.normalizeAssetName(assetName);
        const candidates: { effect: Asset; score: number }[] = [];

        for (const effect of effects) {
            const normalizedEffectName = this.normalizeAssetName(effect.base_name);
            const similarity = this.calculateSimilarity(normalizedTarget, normalizedEffectName);

            if (similarity >= this.fuzzyConfig.minSimilarity) {
                let score = similarity;
                if (effect.formats?.[preferredFormat]) {
                    score += 0.1;
                }
                candidates.push({ effect, score });
            }
        }

        candidates.sort((a, b) => b.score - a.score);
        return candidates.length > 0 ? (candidates[0] ?? null) : null;
    }

    private normalizeAssetName(name: string): string {
        const cacheKey = `normalize:${name}`;
        if (this.normalizationCache.has(cacheKey)) {
            return this.normalizationCache.get(cacheKey)!;
        }

        let normalized = Object.values(this.normalizationRules).reduce((acc, rule) => rule(acc), name);
        this.normalizationCache.set(cacheKey, normalized);
        return normalized;
    }

    private calculateSimilarity(str1: string, str2: string): number {
        const len1 = str1.length;
        const len2 = str2.length;
        if (len1 === 0) return len2 === 0 ? 1 : 0;
        if (len2 === 0) return 0;

        const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));
        for (let i = 0; i <= len1; i++) {
            const row = matrix[i];
            if (row) row[0] = i;
        }
        for (let j = 0; j <= len2; j++) {
            const row = matrix[0];
            if (row) row[j] = j;
        }

        for (let i = 1; i <= len1; i++) {
            for (let j = 1; j <= len2; j++) {
                const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
                const currentRow = matrix[i];
                const prevRow = matrix[i - 1];
                const prevPrevRow = matrix[i - 1];
                if (currentRow && prevRow && prevPrevRow) {
                    currentRow[j] = Math.min(
                        (prevRow[j] ?? 0) + 1,
                        (currentRow[j - 1] ?? 0) + 1,
                        (prevPrevRow[j - 1] ?? 0) + cost
                    );
                }
            }
        }
        const finalRow = matrix[len1];
        const finalValue = finalRow ? finalRow[len2] ?? 0 : 0;
        return (Math.max(len1, len2) - finalValue) / Math.max(len1, len2);
    }

    private buildCommonAliases(): void {
        for (let i = 1; i <= 20; i++) {
            const padded = i.toString().padStart(2, '0');
            this.aliasMap.set(`Electric discharge ${padded}`, `Electricity ${padded}`);
            this.aliasMap.set(`Electric discharge ${i}`, `Electricity ${i}`);
        }
    }

    private getEffectCatalog(): EffectCatalogData | null {
        const registry = useTokchanStore.getState().getMediaPathRegistry() as MediaPathRegistryWithConfigs | undefined;
        return registry?.configs?.effects ?? null;
    }
}

export default SmartAssetResolver;
